import type { MenuProduct } from '@/entities/menu';
import type { BranchProduct } from '../model/schema';

// Step 2 of the branch wizard: which products the branch stocks, and what it
// charges for each. The wire side of that is three separate RPCs on the
// Product Service — attach, re-price, detach — so the work here is turning
// "what the merchant left in the list" into the minimal set of calls.

/**
 * A row seeded from a product the branch ALREADY stocks (an edit).
 *
 * `price` comes from the branch-scoped read, so it is that branch's own
 * overlay rather than the shared base price — seeding the base price would
 * show, and then silently write back, a number the branch never charged.
 */
export function toBranchProduct(product: MenuProduct): BranchProduct {
  return {
    productId: String(product.id),
    name: product.name,
    ...(product.imageUrl ? { imageUrl: product.imageUrl } : {}),
    price: product.price,
    stockQuantity: product.stockQuantity ?? null,
    attached: true,
    // Only the branch's OWN hours: the dish's are inherited, not the branch's.
    ...(product.ownAvailableFrom ? { availableFrom: product.ownAvailableFrom } : {}),
    ...(product.ownAvailableTo ? { availableTo: product.ownAvailableTo } : {}),
  };
}

/**
 * A row for a product the merchant just picked in the picker.
 *
 * The price is seeded from the catalog's price rather than left blank: the
 * merchant-wide read carries the LOWEST price across the branches stocking it,
 * which is a defensible starting point and saves retyping the common case
 * where a new branch charges what the others do. It stays editable, and a
 * product stocked nowhere yet seeds `0`, which the row shows as empty.
 */
export function toPickedProduct(product: MenuProduct): BranchProduct {
  return {
    productId: String(product.id),
    name: product.name,
    ...(product.imageUrl ? { imageUrl: product.imageUrl } : {}),
    price: product.price > 0 ? product.price : null,
    stockQuantity: null,
    attached: false,
  };
}

/** One newly stocked product's branch-local overlay. */
export interface BranchProductAttach {
  productId: string;
  price: number;
  stockQuantity?: number;
  /** The branch's own selling hours; absent = inherit the dish's. */
  availability?: BranchAvailability;
}

/** A branch's own selling hours, "HH:MM"; a side left out is unlimited. */
export interface BranchAvailability {
  from?: string;
  to?: string;
}

/** One product's branch-local overlay change. */
export interface BranchProductPatch {
  productId: string;
  price?: number;
  stockQuantity?: number | null;
  /** `null` = drop the branch's hours and inherit the dish's again. */
  availability?: BranchAvailability | null;
}

/** A row's hours as a window, or `undefined` when the branch sets none. */
function rowAvailability(row: BranchProduct): BranchAvailability | undefined {
  if (!row.availableFrom && !row.availableTo) return undefined;
  return {
    ...(row.availableFrom ? { from: row.availableFrom } : {}),
    ...(row.availableTo ? { to: row.availableTo } : {}),
  };
}

/**
 * What step 2 has to write, as three disjoint sets.
 *
 * Split by what the API can actually do: `POST .../restaurant-products` only
 * ADDS a branch to a product's stocking list (it skips branches already
 * stocking it rather than re-pricing them), `PATCH` only touches rows that
 * already exist, and `DELETE` removes the branch from the product. So a row
 * that changed price needs the PATCH and a newly picked one needs the POST —
 * neither call can stand in for the other.
 */
export interface BranchProductsDiff {
  /** Newly picked rows — attach the branch, carrying its price/count overlay. */
  attach: BranchProductAttach[];
  /** Already-stocked rows whose price or count the merchant edited. */
  reprice: BranchProductPatch[];
  /** Product ids removed from the list — detach the branch. */
  detach: string[];
}

/**
 * Diff the list the merchant left behind against what the branch stocked when
 * the editor opened.
 *
 * `seeded` is the branch's stocked set at open time, NOT the current form rows:
 * the rows carry `attached`, but a row's price also has to be compared against
 * what it *was* to tell an untouched row from a re-priced one, and only the
 * seed holds that.
 *
 * A row with no price entered is attached at 0 rather than skipped — the API
 * defaults an omitted price to 0 anyway, and dropping the row instead would
 * silently discard a product the merchant explicitly picked.
 */
export function diffBranchProducts(
  rows: BranchProduct[],
  seeded: BranchProduct[],
): BranchProductsDiff {
  const seededById = new Map(seeded.map((row) => [row.productId, row]));
  const keptIds = new Set(rows.map((row) => row.productId));

  const attach: BranchProductAttach[] = [];
  const reprice: BranchProductPatch[] = [];

  for (const row of rows) {
    const price = row.price ?? 0;
    const stockQuantity = row.stockQuantity ?? null;
    const previous = seededById.get(row.productId);
    const availability = rowAvailability(row);

    if (previous === undefined) {
      attach.push({
        productId: row.productId,
        price,
        ...(stockQuantity === null ? {} : { stockQuantity }),
        ...(availability === undefined ? {} : { availability }),
      });
      continue;
    }

    const priceChanged = (previous.price ?? 0) !== price;
    const stockChanged = (previous.stockQuantity ?? null) !== stockQuantity;
    const previousAvailability = rowAvailability(previous);
    const availabilityChanged =
      (previousAvailability?.from ?? '') !== (availability?.from ?? '') ||
      (previousAvailability?.to ?? '') !== (availability?.to ?? '');
    // Already stocked. Only a CHANGED overlay is worth a request — re-sending
    // an identical one would queue a pointless write for a moderated merchant.
    if (priceChanged || stockChanged || availabilityChanged) {
      reprice.push({
        productId: row.productId,
        ...(priceChanged ? { price } : {}),
        ...(stockChanged ? { stockQuantity } : {}),
        // Emptied pickers are `null` (inherit the dish's hours), not an empty
        // window — the wire reads absent as "don't touch".
        ...(availabilityChanged ? { availability: availability ?? null } : {}),
      });
    }
  }

  const detach = seeded
    .map((row) => row.productId)
    .filter((productId) => !keptIds.has(productId));

  return { attach, reprice, detach };
}
